import Data, { treeNode } from '@/data'
import { InventorySlotType } from '@/data/enums'
import Store, { defaultPlanner, StoreState } from '@/store'
import { SkillsEmbedSkill, TreeVariant } from '@/types/embed'
import { Gem } from '@/types/item'
import { ItemId, PlayerClass, PobConfigSet, Profile } from '@/types/profile'
import { getUniqueId } from '@maxroll-media-group/common-libs'
import _ from 'lodash'
import { gemCreate, gemName, validateGem } from '../gem'
import { itemParseText } from '../item'
import { passiveNodeLimit, treeCreateHistory, TreeEditingState, TreeProperties } from '../tree'

const classMap: Record<
  number,
  {
    id: PlayerClass
    ascendancies: Record<number, string>
  }
> = {
  // [0]: {
  //   id: 'DexFour',
  //   ascendancies: {
  //     [1]: 'Ranger1',
  //     [2]: 'Ranger3'
  //   }
  // },
  // [1]: {
  //   id: 'StrFourb',
  //   ascendancies: {
  //     [1]: 'Warrior1',
  //     [2]: 'Warrior2'
  //   }
  // },
  // [2]: {
  //   id: 'StrDexFourb',
  //   ascendancies: {
  //     [1]: 'Mercenary2',
  //     [2]: 'Mercenary3'
  //   }
  // },
  // [3]: {
  //   id: 'IntFour',
  //   ascendancies: {
  //     [1]: 'Witch1',
  //     [2]: 'Witch2'
  //   }
  // },
  // [4]: {
  //   id: 'IntFourb',
  //   ascendancies: {
  //     [1]: 'Sorceress1',
  //     [2]: 'Sorceress2'
  //   }
  // },
  // [5]: {
  //   id: 'DexIntFourb',
  //   ascendancies: {
  //     [1]: 'Monk2',
  //     [2]: 'Monk3'
  //   }
  // }
}

function convertSlot(slot: string) {
  switch (slot) {
    case 'Amulet':
      return 'Amulet'
    case 'Belt':
      return 'Belt'
    case 'Body Armour':
      return 'BodyArmour'
    case 'Boots':
      return 'Boots'
    case 'Gloves':
      return 'Gloves'
    case 'Helmet':
      return 'Helm'
    case 'Ring 1':
      return 'Ring'
    case 'Ring 2':
      return 'Ring2'
    case 'Flask 1':
      return 'Flask1'
    case 'Flask 2':
      return 'Flask2'
    case 'Flask 3':
      return 'Flask3'
    case 'Flask 4':
      return 'Flask4'
    case 'Flask 5':
      return 'Flask5'
    case 'Weapon 1':
      return 'Weapon'
    case 'Weapon 2':
      return 'Offhand'
    case 'Weapon 1 Swap':
      return 'Weapon2'
    case 'Weapon 2 Swap':
      return 'Offhand2'
  }
}

async function decompress(text: string) {
  const blob = await (
    await fetch(`data:application/octet-binary;base64,` + text.replace(/-/g, '+').replace(/_/g, '/'))
  ).blob()
  const ds = new DecompressionStream('deflate')
  const decompressed = blob.stream().pipeThrough(ds)
  return new Response(decompressed).text()
}

function createHistory(planner: Profile, variant: TreeVariant, nodes: number[], items: StoreState['items']) {
  const props: TreeProperties = {
    version: planner.version,
    charClass: planner.class,
    ascendancy: planner.ascendancy,
    limit: passiveNodeLimit
  }
  if (variant.charClass) {
    props.charClass = variant.charClass
    props.ascendancy = variant.ascendancy
  }

  const state: TreeEditingState = {
    history: [],
    position: 0,
    // activeSet: 0,
    masteries: variant.masteries,
    jewels: variant.jewels,
    tattoos: variant.tattoos
    // attributes: variant.attributes
  }

  return treeCreateHistory(props, state, nodes, items)
}

function asNumber(str: string | null) {
  if (!str) return
  const num = Number(str)
  return isNaN(num) ? undefined : num
}
function asNumberArray(str: string | null) {
  const result: number[] = []
  if (!str) return result
  for (const p of str.split(',')) {
    const id = Number(p)
    if (!isNaN(id)) result.push(id)
  }
  return result
}

function getAnyValue(elem: Element) {
  const numberAttr = elem.getAttribute('number')
  if (numberAttr !== null) return parseFloat(numberAttr)

  const strAttr = elem.getAttribute('string')
  if (strAttr !== null) return strAttr

  const boolAttr = elem.getAttribute('boolean')
  if (boolAttr !== null) return boolAttr === 'true' || boolAttr === 'True'

  return null
}

function stripTextColors(text?: string | null) {
  if (!text) return text

  return text.replace(/\^[0-9]/g, '').replace(/ \{[0-9]+\}/g, '')
}

export async function parsePob(text: string): Promise<StoreState> {
  const xml = await decompress(text)
  // console.log('xml', xml)
  const parser = new DOMParser()
  const doc = parser.parseFromString(xml, 'application/xml')
  const build = doc.querySelector('Build')
  if (!build) throw Error(`Invalid build string`)

  const className = build.getAttribute('className')
  const playerClass = (Object.keys(Data.characters) as PlayerClass[]).find(
    (id) => Data.characters[id].name === className
  )
  if (!playerClass) throw Error(`Invalid class ${className}`)

  const planner = defaultPlanner(playerClass)
  planner.version = Data.defaultVersion
  planner.passives.variants = [...planner.passives.variants]
  planner.atlas.variants = [...planner.atlas.variants]

  const items: StoreState['items'] = {}

  const ascendancyName = build.getAttribute('ascendClassName')
  const classData = Data.characters[playerClass]
  planner.ascendancy = Object.keys(classData.ascendancies).find(
    (id) => classData.ascendancies[id].name === ascendancyName
  )

  const level = build.getAttribute('level')
  if (level && !isNaN(Number(level))) planner.level = Number(level)

  for (const item of doc.querySelectorAll('Items Item')) {
    const id = asNumber(item.getAttribute('id'))
    if (!id || !item.textContent) continue
    const data = itemParseText(item.textContent, true, true)
    if (data) items[id] = data
  }

  doc.querySelectorAll('Tree Spec').forEach((spec, index) => {
    const variant: TreeVariant = {
      // attributes: {},
      name: stripTextColors(spec.getAttribute('title')) || `Tree ${index + 1}`,
      history: [],
      masteries: {},
      jewels: {},
      tattoos: {},
      notes: []
    }

    const classId = asNumber(spec.getAttribute('classId'))
    const ascendClassId = asNumber(spec.getAttribute('ascendClassId'))
    const charClass = classId ? classMap[classId]?.id : undefined
    const ascendancy = ascendClassId && classId ? classMap[classId]?.ascendancies[ascendClassId] : undefined
    if (charClass && (charClass !== planner.class || ascendancy !== planner.ascendancy)) {
      variant.charClass = charClass
      variant.ascendancy = ascendancy
    }

    const nodes = asNumberArray(spec.getAttribute('nodes'))
    // const weaponSets: number[][] = []
    // const set1 = spec.querySelector('WeaponSet1')
    // const set2 = spec.querySelector('WeaponSet2')
    // if (set1) {
    //   weaponSets.push(asNumberArray(set1.getAttribute('nodes')))
    // }
    // if (set2) {
    //   weaponSets.push(asNumberArray(set2.getAttribute('nodes')))
    // }

    for (const socket of spec.querySelectorAll('Sockets Socket')) {
      const nodeId = asNumber(socket.getAttribute('nodeId'))
      const itemId = asNumber(socket.getAttribute('itemId'))
      if (nodeId && itemId && items[itemId]) {
        variant.jewels[nodeId] = itemId
      }
    }
    for (const mastery of _.trim(spec.getAttribute('masteryEffects') ?? '', '{} ').split('},{')) {
      if (!mastery) continue
      const masteryData = mastery.split(',').map((val) => parseInt(val) ?? -1)
      if (!masteryData) continue
      const node = treeNode(masteryData[0], planner.version)
      if (!node?.mastery?.effectsNodeIds) continue
      const index = node.mastery.effectsNodeIds.indexOf(masteryData[1])
      variant.masteries[masteryData[0]] = index
    }

    for (const socket of spec.querySelectorAll('Overrides Override')) {
      const nodeId = asNumber(socket.getAttribute('nodeId'))
      const overrideId = socket.getAttribute('dn')
      if (nodeId && overrideId) {
        const tattoo = Object.entries(Data.tattoos).find(([, tattoo]) => tattoo.name === overrideId)
        if (tattoo) {
          variant.tattoos[nodeId] = tattoo[0]
        }
      }
    }

    // const attrib = spec.querySelector('Overrides AttributeOverride')
    // if (attrib) {
    //   for (const id of asNumberArray(attrib.getAttribute('strNodes'))) {
    //     variant.attributes[id] = 0
    //   }
    //   for (const id of asNumberArray(attrib.getAttribute('dexNodes'))) {
    //     variant.attributes[id] = 1
    //   }
    //   for (const id of asNumberArray(attrib.getAttribute('intNodes'))) {
    //     variant.attributes[id] = 2
    //   }
    // }

    // console.log('var jewels', index, variant)

    variant.history = createHistory(planner, variant, nodes, items)

    planner.passives.variants[index] = variant
  })

  const pobItemSetIdToIndex: Record<string, number> = {}
  doc.querySelectorAll('Items ItemSet').forEach((itemSet, index) => {
    const variant: {
      id: string
      name?: string
      items: {
        [key in InventorySlotType]?: ItemId
      }
    } = {
      id: getUniqueId(),
      items: {}
    }
    for (const slot of itemSet.querySelectorAll('Slot')) {
      const name = slot.getAttribute('name')
      const id = asNumber(slot.getAttribute('itemId'))
      if (name && id && items[id]) {
        const slot = convertSlot(name)
        if (slot) variant.items[slot] = id
      }
    }
    // for (const slot of itemSet.querySelectorAll('Slot')) {
    //   const name = slot.getAttribute('name')
    //   const id = asNumber(slot.getAttribute('itemId'))
    //   if (name && id && items[id]) {
    //     const m = name.match(/^(.*) Socket (\d+)$/)
    //     if (!m) continue
    //     const slot = convertSlot(m[1])
    //     if (!slot) continue
    //     const itemId = variant.items[slot]
    //     if (!itemId) continue
    //     const item = items[itemId]
    //     const rune = items[id]
    //     if (!item || !rune) continue
    //     item.sockets.push(rune.base)
    //   }
    // }

    const name = stripTextColors(itemSet.getAttribute('title'))
    if (name) variant.name = name

    planner.equipment.variants[index] = variant

    const pobSetId = itemSet.getAttribute('id')
    if (pobSetId) pobItemSetIdToIndex[pobSetId] = index
  })

  const pobActiveSetId = doc.querySelectorAll('Items')[0].getAttribute('activeItemSet') ?? ''
  planner.equipment.active = pobItemSetIdToIndex[pobActiveSetId] || 0

  const gemMap: Record<string, string> = {}
  for (const [id, gem] of Object.entries(Data.gems)) {
    if (validateGem(gem, id)) {
      gemMap[gemName(gem)] = id
    }
  }

  const pobActiveSkillSetId = doc.querySelectorAll('Skills')[0].getAttribute('activeSkillSet') || '1'
  const pobActiveSkillGemId = build.getAttribute('mainSocketGroup') || '1'

  let mainSkillGemIndex = undefined
  let mainSkillPartIndex = undefined
  let activeSkillSetIndex = parseInt(pobActiveSkillSetId) - 1 || 0
  let activeSkillGemIndex = parseInt(pobActiveSkillGemId) - 1 || 0

  doc.querySelectorAll('Skills SkillSet').forEach((skillSet, index) => {
    const skills: SkillsEmbedSkill[] = []
    let skillIndex = 0
    for (const skill of skillSet.querySelectorAll('Skill')) {
      const gems: Gem[] = []
      for (const gem of skill.querySelectorAll('Gem')) {
        const gemId = gem.getAttribute('gemId')
        const rawItem = gemId ? Data.items[gemId] : undefined
        if (!rawItem) continue
        const level = asNumber(gem.getAttribute('level'))
        const quality = asNumber(gem.getAttribute('quality'))
        const newGemId = gemMap[rawItem.name]
        const item = gemCreate(newGemId)
        if (level) item.level = level
        if (quality) item.quality = quality
        gems.push(item)
      }

      if (gems.length > 0) {
        const skillStep: SkillsEmbedSkill = { gems }
        const slot = skill.getAttribute('slot')
        if (slot) skillStep.slot = convertSlot(slot)
        skills.push(skillStep)
      }

      const id = skillSet.getAttribute('id') ?? `${index + 1}`
      const skillId = skill.getAttribute('id') ?? `${skillIndex + 1}`
      if (id === pobActiveSkillSetId && skillId === pobActiveSkillGemId) {
        activeSkillSetIndex = index
        activeSkillGemIndex = skillIndex
        mainSkillGemIndex = parseInt(skill.getAttribute('mainActiveSkillCalcs') || '1') - 1
        mainSkillPartIndex = parseInt(skill.getAttribute('mainActiveSkill') || '1') - 1
      }

      skillIndex += 1
    }

    const name = stripTextColors(skillSet.getAttribute('title'))

    planner.skills.steps[index] = {
      name: name || `Set ${index + 1}`,
      skills
    }
  })

  planner.skills.step = activeSkillSetIndex

  const stats: Record<string, number> = {}
  build.querySelectorAll('PlayerStat').forEach((playerStat, index) => {
    const stat = playerStat.getAttribute('stat')
    const value = playerStat.getAttribute('value')
    if (stat === null || value === null) return

    stats[stat] = parseFloat(value)
  })

  const buffs = build.querySelector('Buffs')
  const configSummary = {
    combatList:
      buffs
        ?.getAttribute('combatList')
        ?.split(',')
        .map((item) => item.trim()) || [],
    buffList:
      buffs
        ?.getAttribute('bufflist')
        ?.split(',')
        .map((item) => item.trim()) || [],
    curseList:
      buffs
        ?.getAttribute('curselist')
        ?.split(',')
        .map((item) => item.trim()) || []
  }

  const config: PobConfigSet[] = []
  doc.querySelectorAll('Config ConfigSet').forEach((configSet, index) => {
    const placeholder: Record<string, string | number | boolean> = {}
    for (const placeholderItem of configSet.querySelectorAll('Placeholder')) {
      const name = placeholderItem.getAttribute('name')
      const value = getAnyValue(placeholderItem)
      if (name === null || value === null) return

      placeholder[name] = value
    }

    const input: Record<string, string | number | boolean> = {}
    for (const inputItem of configSet.querySelectorAll('Input')) {
      const name = inputItem.getAttribute('name')
      const value = getAnyValue(inputItem)
      if (name === null || value === null) return

      input[name] = value
    }

    config.push({ placeholder, input })
  })

  const notes = doc.querySelectorAll('Notes')[0].textContent?.trim()

  const majorGod = config[0]?.input?.['pantheonMajorGod']
  const minorGod = config[0]?.input?.['pantheonMinorGod']
  const bandit = config[0]?.input?.['bandit']

  planner.bandits = `${bandit || 'eramir'}`.toLowerCase()
  if (majorGod) planner.pantheon[`${majorGod}`] = [true, true, true]
  if (minorGod) planner.pantheon[`${minorGod}`] = [true]

  const pobState: StoreState = {
    id: Store.newId(),
    embeds: [],
    activeEmbed: -1,
    items,
    planner,
    pobBuild: {
      code: text,
      stats,
      config,
      configSummary,
      activeSkill: {
        skillSet: activeSkillSetIndex,
        skillGem: activeSkillGemIndex,
        skillCalcs: mainSkillGemIndex,
        skillPart: mainSkillPartIndex
      },
      notes
    }
  }

  return pobState
}
